import {useState, useRef, useEffect} from 'react';
import styled from 'styled-components';
import {raisedSurfaceShadow} from '../../../../styles/elevation';
import type {MentionUser} from '../../hooks/useMentionUsers';

interface Props {
	assigneeId: string | null;
	users: MentionUser[];
	onAssign: (userId: string | null, note?: string) => void;
	onDelete?: () => void;
	disabled?: boolean;
}

const Container = styled.div`
	position: relative;
`;

// Hidden until the surrounding card is hovered (TicketCard's Card targets
// [data-kebab] on hover) or the button is focused — replaces group-hover.
const MenuButton = styled.button<{$disabled: boolean}>`
	display: flex;
	height: 24px;
	width: 24px;
	align-items: center;
	justify-content: center;
	border: 0;
	border-radius: 4px;
	background: transparent;
	color: #94a3b8;
	cursor: pointer;
	opacity: ${p => (p.$disabled ? 0.3 : 0)};
	transition: opacity 0.15s;
	${p => p.$disabled && 'cursor: not-allowed;'}
	&:hover {
		background: #f1f5f9;
		color: #475569;
	}
	&:focus {
		opacity: 1;
	}
`;

const KebabIcon = styled.svg`
	height: 16px;
	width: 16px;
`;

const Popover = styled.div`
	position: absolute;
	right: 0;
	top: 100%;
	z-index: 50;
	margin-top: 4px;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	${raisedSurfaceShadow}
`;

const Menu = styled(Popover)`
	width: 208px;
`;

const MenuHeader = styled.div`
	border-bottom: 1px solid #f1f5f9;
	padding: 6px 12px;
`;

const MenuHeaderText = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;

const MenuList = styled.ul`
	margin: 0;
	padding: 4px 0;
	max-height: 240px;
	overflow-y: auto;
	list-style: none;
`;

const MenuItem = styled.button<{$active?: boolean; $muted?: boolean}>`
	display: flex;
	width: 100%;
	align-items: center;
	gap: 8px;
	border: 0;
	padding: 6px 12px;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: ${p => (p.$active ? 500 : 400)};
	background: ${p => (p.$active ? '#eff6ff' : 'transparent')};
	color: ${p => (p.$active ? '#1d4ed8' : p.$muted ? '#64748b' : '#1e293b')};
	cursor: pointer;
	&:hover {
		background: ${p => (p.$active ? '#eff6ff' : '#f8fafc')};
	}
`;

const Avatar = styled.span<{$variant: 'user' | 'unassigned'}>`
	display: inline-flex;
	height: 24px;
	width: 24px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: 9999px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	background: ${p => (p.$variant === 'user' ? '#e0e7ff' : '#f1f5f9')};
	color: ${p => (p.$variant === 'user' ? '#4338ca' : '#64748b')};
`;

const UserName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const MenuDivider = styled.div`
	border-top: 1px solid #f1f5f9;
`;

const DeleteItem = styled.button`
	display: flex;
	width: 100%;
	align-items: center;
	gap: 8px;
	border: 0;
	background: transparent;
	padding: 6px 12px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #dc2626;
	cursor: pointer;
	&:hover {
		background: #fef2f2;
	}
`;

const ConfirmDialog = styled(Popover)`
	width: 256px;
`;

const ConfirmHeader = styled.div`
	border-bottom: 1px solid #f1f5f9;
	padding: 8px 12px;
`;

const ConfirmTitle = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 600;
	color: #334155;
`;

const ConfirmBody = styled.div`
	padding: 12px;
`;

const NoteLabel = styled.label`
	display: block;
	margin-bottom: 4px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #64748b;
`;

const NoteTextarea = styled.textarea`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	padding: 4px 8px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #1e293b;
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const ConfirmActions = styled.div`
	margin-top: 8px;
	display: flex;
	gap: 8px;
`;

const ConfirmButton = styled.button`
	flex: 1 1 0%;
	border: 0;
	border-radius: 4px;
	background: #2563eb;
	padding: 4px 12px;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;
	&:hover {
		background: #1d4ed8;
	}
`;

const SkipButton = styled.button`
	flex: 1 1 0%;
	border: 1px solid #e2e8f0;
	border-radius: 4px;
	background: transparent;
	padding: 4px 12px;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #475569;
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
`;

function UserInitials({name}: {name: string}) {
	const init = name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(s => s[0]!.toUpperCase())
		.join('');
	return <Avatar $variant="user">{init}</Avatar>;
}

export function CardKebabMenu({assigneeId, users, onAssign, onDelete, disabled = false}: Props) {
	const [open, setOpen] = useState(false);
	const [pendingUserId, setPendingUserId] = useState<string | null | undefined>(undefined);
	const [note, setNote] = useState('');
	const containerRef = useRef<HTMLDivElement>(null);

	const confirming = pendingUserId !== undefined;

	useEffect(() => {
		if (!open) return;
		function handleClick(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
				setPendingUserId(undefined);
				setNote('');
			}
		}
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Escape') {
				setOpen(false);
				setPendingUserId(undefined);
				setNote('');
			}
		}
		document.addEventListener('mousedown', handleClick);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handleClick);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [open]);

	function handleSelect(userId: string | null) {
		if (userId === null) {
			onAssign(null, undefined);
			setOpen(false);
			return;
		}
		if (userId === assigneeId) {
			setOpen(false);
			return;
		}
		setPendingUserId(userId);
		setNote('');
	}

	function handleConfirm() {
		const trimmed = note.trim();
		onAssign(pendingUserId!, trimmed.length > 0 ? trimmed : undefined);
		setOpen(false);
		setPendingUserId(undefined);
		setNote('');
	}

	function handleSkip() {
		onAssign(pendingUserId!, undefined);
		setOpen(false);
		setPendingUserId(undefined);
		setNote('');
	}

	const pendingUser = pendingUserId ? users.find(u => u.id === pendingUserId) : null;

	return (
		<Container
			ref={containerRef}
			// Prevent dnd-kit from starting a drag when interacting with the menu
			onPointerDown={e => e.stopPropagation()}
		>
			<MenuButton
				type="button"
				data-kebab
				aria-label="Ticket actions"
				aria-haspopup="true"
				aria-expanded={open}
				disabled={disabled}
				$disabled={disabled}
				onClick={() => {
					if (!disabled) setOpen(o => !o);
				}}
			>
				<KebabIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
					<circle cx="10" cy="4" r="1.5" />
					<circle cx="10" cy="10" r="1.5" />
					<circle cx="10" cy="16" r="1.5" />
				</KebabIcon>
			</MenuButton>

			{open && !confirming && (
				<Menu role="menu">
					<MenuHeader>
						<MenuHeaderText>Assign to</MenuHeaderText>
					</MenuHeader>
					<MenuList>
						<li>
							<MenuItem
								type="button"
								role="menuitem"
								onClick={() => handleSelect(null)}
								$active={assigneeId === null}
								$muted
							>
								<Avatar $variant="unassigned">?</Avatar>
								<span>Unassigned</span>
							</MenuItem>
						</li>
						{users.map(user => (
							<li key={user.id}>
								<MenuItem
									type="button"
									role="menuitem"
									onClick={() => handleSelect(user.id)}
									$active={user.id === assigneeId}
								>
									<UserInitials name={user.name} />
									<UserName>{user.name}</UserName>
								</MenuItem>
							</li>
						))}
					</MenuList>
					{onDelete && (
						<>
							<MenuDivider />
							<DeleteItem
								type="button"
								role="menuitem"
								onClick={() => {
									onDelete();
									setOpen(false);
								}}
							>
								Delete ticket
							</DeleteItem>
						</>
					)}
				</Menu>
			)}

			{open && confirming && (
				<ConfirmDialog role="dialog" aria-label="Handoff note">
					<ConfirmHeader>
						<ConfirmTitle>
							Assign to <strong>{pendingUser?.name ?? pendingUserId}</strong>
						</ConfirmTitle>
					</ConfirmHeader>
					<ConfirmBody>
						<NoteLabel>Handoff note (optional)</NoteLabel>
						<NoteTextarea
							value={note}
							onChange={e => setNote(e.target.value)}
							placeholder="Add a note for the new assignee..."
							rows={3}
						/>
						<ConfirmActions>
							<ConfirmButton type="button" onClick={handleConfirm}>
								Confirm
							</ConfirmButton>
							<SkipButton type="button" onClick={handleSkip}>
								Skip
							</SkipButton>
						</ConfirmActions>
					</ConfirmBody>
				</ConfirmDialog>
			)}
		</Container>
	);
}
